import type { PointerEvent, ReactNode, TouchEvent } from 'react';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import throttle from 'lodash.throttle';
import { createPortal } from 'react-dom';
import styled, {
  DefaultTheme,
  Interpolation,
  ThemeProps,
} from 'styled-components';

import { Component as StaticIcon } from '../StaticIcon';
import ToastMessages from '../ToastMessages/ToastMessages';

export interface BottomSheetRef {
  scrollContainer: HTMLDivElement | null;
  open: () => void;
  close: () => void;
  isOpened: boolean;
}

type TransitionProps = {
  enterDuration: string;
  exitDuration: string;
  enterEase: string;
  exitEase: string;
};

interface BottomSheetStyling {
  closeIconStyling?: Interpolation<ThemeProps<DefaultTheme>>;
  headerStyling?: Interpolation<ThemeProps<DefaultTheme>>;
  contentStyling?: Interpolation<ThemeProps<DefaultTheme>>;

  [key: string]: Interpolation<ThemeProps<DefaultTheme>>;
}

type ExtendableStyledComponent<k extends string> = {
  [key in k]: Interpolation<ThemeProps<DefaultTheme>>;
};

export interface BottomSheetProps {
  children?: ReactNode;
  className?: string;
  contentPadding?: number;
  expansionSwitchThreshold?: number;
  permanent?: boolean;
  enableFooterAnimations?: boolean;
  hideCollapsableFooter?: boolean;
  transitionProps?: TransitionProps;
  detente?: number;
  scrollToTopOnOpen?: boolean;
  showToastMessages?: boolean;
  showHeaderSeparator?: boolean;
  onClose: () => void;
  title: ReactNode;
  footer: ReactNode;
  styles?: BottomSheetStyling;
}

export const BOTTOM_SHEET_CONTENT_SIDE_PADDING = 16;

const DETENTE_VALUE = 0.97;
export const BOTTOM_SHEET_DEFAULT_TRANSITION_PROPS_TIMINGS = {
  enter: 500,
  exit: 250,
};
const DEFAULT_TRANSITION_PROPS = {
  enterDuration: `${BOTTOM_SHEET_DEFAULT_TRANSITION_PROPS_TIMINGS.enter}ms`,
  exitDuration: `${BOTTOM_SHEET_DEFAULT_TRANSITION_PROPS_TIMINGS.exit}ms`,
  enterEase: 'ease-out',
  exitEase: 'ease-in',
};

const HEADER_MINIMIZE_THRESHOLD = 50;
const HEADER_DEFAULT_TRANSITION = `padding 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)`;

const SheetContainer = styled.div<{
  isOpen: boolean;
  isHidden: boolean;
}>`
  overflow: hidden;
  overscroll-behavior: none;
  position: fixed;
  pointer-events: none;
  right: 0;
  left: 0;
  ${({ isHidden }) => isHidden && `opacity: 0`};
`;

const Dim = styled.div<{ isOpened: boolean }>`
  pointer-events: ${({ isOpened }) => (isOpened ? 'all' : 'none')};
  background: ${({ theme }) => theme.colors.black900};
  opacity: ${({ isOpened }) => (isOpened ? '0.5' : '0')};
  position: fixed;
  inset: 0;
`;

const SheetContent = styled.div<{
  resultingTransform: number;
  transform: number;
  isOpen: boolean;
  transitionProps: TransitionProps;
  detenteValue: number;
}>`
  display: flex;
  position: fixed;
  right: 0;
  left: 0;
  bottom: 0;
  flex-direction: column;
  flex-shrink: 0;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  width: 100%;
  max-width: 48rem;
  background-color: ${({ theme }) => theme.colors.white};
  pointer-events: auto;
  will-change: transform;
  transition: ${({ isOpen, transitionProps }) =>
    `transform ${
      isOpen ? transitionProps.enterDuration : transitionProps.exitDuration
    } ${isOpen ? transitionProps.enterEase : transitionProps.exitEase}`};
  height: ${props => props.detenteValue}px;
  transform: ${props =>
    `translate3d(0, calc(100% + ${props.resultingTransform}px), 0)`};
`;

const Grabber = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 12px;
  flex-shrink: 0;
  cursor: ns-resize;
  touch-action: none;
`;

const GrabberBar = styled.span`
  border-radius: 9999px;
  align-self: center;
  width: 38px;
  height: 3px;
  background: #d9d9d9;
`;

const CloseIcon = styled(StaticIcon).attrs(props => {
  return {
    type: 'close',
    size: 14,
    color: props.theme.colors.subdued,
    'data-testid': 'BottomDrawerCloseIcon',
  };
})<ExtendableStyledComponent<'closeIconStyling'>>`
  stroke: ${props => props.theme.colors.subdued};
  stroke-width: 1px;
  ${({ closeIconStyling }) => closeIconStyling};
`;

const HeaderWrapper = styled.div<{
  isMinimized: boolean;
  showHeaderSeparator: boolean;
}>`
  padding: ${({ isMinimized }) => (isMinimized ? 0 : `0 16px`)};
  transition: ${HEADER_DEFAULT_TRANSITION};

  ${({ isMinimized, showHeaderSeparator, theme }) =>
    isMinimized &&
    showHeaderSeparator &&
    `border-bottom: 1px solid ${theme.colors.stone};`};
`;
const Header = styled.div<
  ExtendableStyledComponent<'headerStyling'> & { isMinimized: boolean }
>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ isMinimized }) => (isMinimized ? `16px` : `24px 0`)};
  border-bottom: 1px solid ${props => props.theme.colors.gray250};
  transition: ${HEADER_DEFAULT_TRANSITION};
  ${({ headerStyling }) => headerStyling};
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 700;
`;

const HeaderIcon = styled.div`
  transition: ${HEADER_DEFAULT_TRANSITION};
`;

const Footer = styled.div<{
  showFooterShadow: boolean;
  enableFooterAnimations: boolean;
  hidden: boolean;
}>`
  display: flex;
  justify-content: space-around;
  position: sticky;
  bottom: 0;
  padding: 18px 16px;
  gap: 12px;
  transition: box-shadow 0.1s ease-out;
  ${({ showFooterShadow }) =>
    showFooterShadow && `box-shadow: 0 -2px 8px 0 #18181e1a;`}
  ${({ enableFooterAnimations }) =>
    enableFooterAnimations && `transition: all 0.1s ease-in-out;`}
  ${({ hidden }) =>
    hidden &&
    `
    height: 0;
    padding-top: 0;
    padding-bottom: 0;
    overflow: hidden;
  `}
`;

const Content = styled.div<
  ExtendableStyledComponent<'contentStyling'> & {
    detenteValue: number;
    resultingTransform: number;
    isOpened: boolean;
    transform: number;
    contentPadding: number;
  }
>`
  margin-bottom: ${({ detenteValue, resultingTransform }) =>
    `${detenteValue + resultingTransform}px`};
  padding: 24px ${props => props.contentPadding}px 0;

  overflow-y: auto;
  height: 100%;
  overscroll-behavior: ${({ transform }) =>
    transform !== 0 ? 'none' : 'contain'};
  ${({ contentStyling }) => contentStyling};
`;

const StyledToastMessages = styled(ToastMessages)`
  top: 0;
  position: fixed;
`;

const BottomSheet = forwardRef<BottomSheetRef, BottomSheetProps>(
  function BottomSheet(
    {
      children,
      className = '',
      contentPadding = BOTTOM_SHEET_CONTENT_SIDE_PADDING,
      expansionSwitchThreshold = 50,
      permanent = false,
      onClose,
      transitionProps = DEFAULT_TRANSITION_PROPS,
      detente = DETENTE_VALUE,
      scrollToTopOnOpen = false,
      enableFooterAnimations = false,
      hideCollapsableFooter = false,
      showToastMessages = false,
      showHeaderSeparator = false,
      title,
      footer,
      styles = {},
    }: BottomSheetProps,
    ref
  ) {
    const { closeIconStyling, headerStyling, contentStyling } = styles;

    const [viewportHeight, setViewportHeight] = useState(0);
    const detenteValue = useMemo(
      () => detente * viewportHeight,
      [viewportHeight, detente]
    );
    const [origin, setOrigin] = useState<number | null>(null);
    const [transform, setTransform] = useState<number>(0);
    const [showFooterShadow, setShowFooterShadow] = useState<boolean>(true);
    const [shouldMinimizeHeader, setShouldMinimizeHeader] =
      useState<boolean>(true);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const sheetContainerRef = useRef<HTMLDivElement | null>(null);

    const [isOpen, setIsOpenInternal] = useState(permanent);
    const [isHidden, setIsHidden] = useState(!isOpen);

    const setIsOpen = useCallback(
      (shouldOpen: boolean) => {
        if (!shouldOpen) {
          onClose();
        }
        if (shouldOpen && scrollToTopOnOpen) {
          scrollContainerRef.current?.scrollTo(0, 0);
        }
        setIsOpenInternal(shouldOpen);
        setIsHidden(false);
      },
      [onClose, setIsOpenInternal, scrollToTopOnOpen, setIsHidden]
    );

    useEffect(() => {
      const preventBodyScroll = isOpen ? 'hidden' : 'visible';
      if (typeof document !== 'undefined') {
        document.body.style.overflow = preventBodyScroll;
      }
    }, [isOpen]);

    useEffect(() => {
      const onScroll = () => {
        const {
          clientHeight = 0,
          scrollTop = 0,
          scrollHeight = 0,
        } = scrollContainerRef.current || {};
        setShowFooterShadow(scrollTop + clientHeight < scrollHeight);
        setShouldMinimizeHeader(scrollTop >= HEADER_MINIMIZE_THRESHOLD);
      };
      scrollContainerRef.current?.addEventListener('scroll', onScroll);
      onScroll();

      if (typeof window !== 'undefined') {
        const resizeListener = throttle(() => {
          setViewportHeight(window.innerHeight);
        }, 100);
        setViewportHeight(window.innerHeight);
        window.addEventListener('resize', resizeListener);
        return () => {
          window.removeEventListener('resize', resizeListener);
        };
      }
    }, []);

    const handleDismiss = useCallback(
      e => {
        e.preventDefault();
        if (!permanent) {
          setIsOpen(false);
        }
      },
      [permanent, setIsOpen]
    );

    const handleGestureStart = useCallback((y: number) => {
      setOrigin(y);
    }, []);

    const handleGestureMove = useCallback(
      (y: number) => {
        if (origin !== null) {
          const delta = y - origin;
          setTransform(delta);
        }
      },
      [origin]
    );

    const handleGestureEnd = useCallback(() => {
      if (transform > expansionSwitchThreshold) {
        if (!permanent) {
          setIsOpen(false);
        } else {
          scrollContainerRef.current?.scrollTo(0, 0);
        }
      }
      setOrigin(null);
      setTransform(0);
    }, [expansionSwitchThreshold, permanent, transform, setIsOpen]);

    const handleGestureCancel = useCallback(() => {
      setOrigin(null);
      setTransform(0);
    }, []);

    const handleTouchStart = useCallback(
      (e: TouchEvent) => {
        const scrollTop = scrollContainerRef.current?.scrollTop;
        if (scrollTop !== undefined && scrollTop <= 0) {
          const y = e.touches[0].pageY;
          handleGestureStart(y);
        }
      },
      [handleGestureStart]
    );
    const handleTouchMove = useCallback(
      (e: TouchEvent) => {
        const y = e.touches[0].pageY;
        handleGestureMove(y);
      },
      [handleGestureMove]
    );

    const handlePointerDown = useCallback(
      (e: PointerEvent) => {
        handleGestureStart(e.clientY);
      },
      [handleGestureStart]
    );
    const handlePointerMove = useCallback(
      (e: PointerEvent) => {
        handleGestureMove(e.clientY);
      },
      [handleGestureMove]
    );

    useImperativeHandle(
      ref,
      () => {
        return {
          scrollContainer: scrollContainerRef.current,
          open() {
            setIsOpen(true);
          },
          close() {
            setIsOpen(false);
          },
          isOpened: isOpen,
        };
      },
      [isOpen, setIsOpen]
    );

    const resultingTransform = Math.max(
      transform + (isOpen ? -detenteValue : 0),
      -detenteValue
    );

    const content = (
      <SheetContainer
        ref={sheetContainerRef}
        id="bottom-sheet-container"
        data-testId="bottom-sheet-container"
        onPointerMove={handlePointerMove}
        onPointerUp={handleGestureEnd}
        isOpen={isOpen}
        isHidden={isHidden}
        onTransitionEnd={() => setIsHidden(!isOpen)}
      >
        <Dim
          data-testid="bottom-sheet-dim"
          isOpened={isOpen}
          onClick={handleDismiss}
        />
        {showToastMessages && (
          <StyledToastMessages data-autotag="mini-cart-toast-message" />
        )}
        <SheetContent
          aria-modal
          role="dialog"
          resultingTransform={resultingTransform}
          className={className}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleGestureEnd}
          onTouchCancel={handleGestureCancel}
          transform={transform}
          isOpen={isOpen}
          transitionProps={transitionProps}
          detenteValue={detenteValue}
        >
          <Grabber
            data-testid="bottom-sheet-grabber"
            onPointerDown={handlePointerDown}
            onPointerCancel={handleGestureCancel}
          >
            <GrabberBar />
            <HeaderWrapper
              isMinimized={shouldMinimizeHeader}
              showHeaderSeparator={showHeaderSeparator}
            >
              <Header
                headerStyling={headerStyling}
                isMinimized={shouldMinimizeHeader}
              >
                <Title>{title}</Title>
                <HeaderIcon
                  data-testid="bottom-sheet-close-icon"
                  onClick={() => setIsOpen(false)}
                >
                  <CloseIcon closeIconStyling={closeIconStyling} />
                </HeaderIcon>
              </Header>
            </HeaderWrapper>
          </Grabber>
          <Content
            data-testid="bottom-sheet-content"
            detenteValue={detenteValue}
            resultingTransform={resultingTransform}
            transform={transform}
            ref={scrollContainerRef}
            isOpened={isOpen}
            contentPadding={contentPadding}
            contentStyling={contentStyling}
          >
            {children}
          </Content>
          {footer && (
            <Footer
              showFooterShadow={showFooterShadow}
              hidden={hideCollapsableFooter}
              enableFooterAnimations={enableFooterAnimations}
            >
              {footer}
            </Footer>
          )}
        </SheetContent>
      </SheetContainer>
    );

    return typeof window === 'undefined'
      ? null
      : createPortal(content, global.document?.body);
  }
);

const MemoizedSheet = memo(BottomSheet);

export default MemoizedSheet;
